import React, { useEffect, useMemo, useState } from "react";
import { MainLayout } from "components/layout/MainLayout";
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Modal,
  PermissionGate,
} from "components/ui";
import { useToast } from "hooks/useToast";
import { usePermission } from "hooks/usePermissions";
import { PageId, ActionCode, UserRole } from "types/index";
import { IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "constants/index";
import {
  AddOutlined,
  VisibilityOutlined,
  EditOutlined,
  DeleteOutline,
} from "@mui/icons-material";
import { employeesService } from "services/employees.service";
import { Employee } from "types/employee";
import { useAuth } from "hooks/useAuth";

export const EmployeesPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  const canView = usePermission(PageId.EMPLOYEES, ActionCode.VIEW);
  const canAdd = usePermission(PageId.EMPLOYEES, ActionCode.ADD);
  const canEdit = usePermission(PageId.EMPLOYEES, ActionCode.EDIT);
  const canDelete = usePermission(PageId.EMPLOYEES, ActionCode.DELETE);

  const [page, setPage] = useState(1);
  const pageSize = 100;
  const [rows, setRows] = useState<Employee[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [companyMap, setCompanyMap] = useState<Record<number, string>>({});

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pagedRows = rows;
  const isAdmin = user?.roleId === UserRole.ADMIN;
  const columnCount = 7 + (isAdmin ? 1 : 0);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await employeesService.list(page, pageSize);
        setRows(res.data);
        setTotal(res.pagination.total);
      } catch (e: any) {
        const friendly =
          "An error occurred on the server. Please contact the administrator.";
        setError(e?.message || friendly);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [page]);

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1));

  const onAdd = () => {
    navigate(ROUTES.EMPLOYEE_NEW);
  };

  const onView = (id: string) => {
    navigate(`${ROUTES.EMPLOYEES}/${id}/view`);
  };

  const onRequestDelete = (id: string) => {
    setDeletingId(id);
    setConfirmOpen(true);
  };

  const onConfirmDelete = async () => {
    if (!deletingId) return;
    try {
      await employeesService.remove(deletingId);
      const res = await employeesService.list(page, pageSize);
      setRows(res.data);
      setTotal(res.pagination.total);
      toast.success("Employee deleted successfully");
    } catch (e: any) {
      const friendly =
        "An error occurred on the server. Please contact the administrator.";
      toast.error(e?.message || friendly);
    } finally {
      setConfirmOpen(false);
      setDeletingId(null);
    }
  };

  return (
    <MainLayout>
      <Card>
        <CardHeader title="Employees" />
        <CardBody>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <div className="text-sm text-secondary-600">
              Page {page} of {totalPages}
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              {canAdd && (
                <Button variant="primary" size="sm" onClick={onAdd} className="w-full sm:w-auto">
                  <AddOutlined fontSize="small" />
                  <span>Add</span>
                </Button>
              )}
            </div>
          </div>

          {error && <div className="mb-3 text-sm text-red-600">{error}</div>}
          {isLoading && (
            <div className="mb-3 text-sm text-secondary-600">Loading...</div>
          )}

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {!isLoading && !error && pagedRows.length === 0 && (
              <div className="p-6 text-center text-secondary-600">No data</div>
            )}
            {pagedRows.map((emp) => (
              <div
                key={emp.id}
                className="bg-white border border-secondary-200 rounded-lg p-4 space-y-3"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-secondary-900 text-base">
                      {emp.name}
                    </h3>
                    {emp.title && (
                      <p className="text-sm text-secondary-600 mt-1">
                        {emp.title}
                      </p>
                    )}
                  </div>
                  <span
                    className={
                      `inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border flex-shrink-0 ml-2 ` +
                      (emp.isActive
                        ? "bg-green-100 text-green-700 border-green-200"
                        : "bg-red-100 text-red-700 border-red-200")
                    }
                  >
                    {emp.isActive ? "Active" : "Not Active"}
                  </span>
                </div>
                <div className="space-y-2 text-sm">
                  {isAdmin && emp.company && (
                    <div className="flex items-center gap-2">
                      <span className="text-secondary-500 font-medium min-w-[80px]">
                        Company:
                      </span>
                      <span className="text-secondary-900">{emp.company}</span>
                    </div>
                  )}
                  {emp.email && (
                    <div className="flex items-center gap-2">
                      <span className="text-secondary-500 font-medium min-w-[80px]">
                        Email:
                      </span>
                      <span className="text-secondary-900 break-all">
                        {emp.email}
                      </span>
                    </div>
                  )}
                  {emp.mobileNumber && (
                    <div className="flex items-center gap-2">
                      <span className="text-secondary-500 font-medium min-w-[80px]">
                        Mobile:
                      </span>
                      <span className="text-secondary-900">
                        {emp.mobileNumber}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-secondary-500 font-medium min-w-[80px]">
                      Scans:
                    </span>
                    <span className="text-secondary-900">
                      {emp.totalScans !== undefined ? emp.totalScans : "—"}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-2 border-t border-secondary-100">
                  {canView && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onView(emp.id)}
                      className="flex-1"
                    >
                      <VisibilityOutlined fontSize="small" />
                      <span>View</span>
                    </Button>
                  )}
                  {canEdit && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        navigate(`${ROUTES.EMPLOYEES}/${emp.id}/edit`)
                      }
                      className="flex-1"
                    >
                      <EditOutlined fontSize="small" />
                      <span>Edit</span>
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border border-secondary-200 rounded-md overflow-hidden">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Name
                  </th>
                  {isAdmin && (
                    <th className="text-left p-3 text-secondary-700 font-semibold">
                      Company
                    </th>
                  )}
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Title
                  </th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Email
                  </th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Mobile Number
                  </th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Total Scans
                  </th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Status
                  </th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {!isLoading && !error && pagedRows.length === 0 && (
                  <tr>
                    <td
                      className="p-6 text-center text-secondary-600"
                      colSpan={columnCount}
                    >
                      No data
                    </td>
                  </tr>
                )}
                {pagedRows.map((emp) => (
                  <tr
                    key={emp.id}
                    className="border-t border-secondary-100 hover:bg-secondary-50"
                  >
                    <td className="p-3">{emp.name}</td>
                    {isAdmin && <td className="p-3">{emp.company}</td>}
                    <td className="p-3">{emp.title}</td>
                    <td className="p-3">{emp.email}</td>
                    <td className="p-3">{emp.mobileNumber}</td>
                    <td className="p-3">
                      {emp.totalScans !== undefined ? emp.totalScans : "—"}
                    </td>
                    <td className="p-3">
                      <span
                        className={
                          `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ` +
                          (emp.isActive
                            ? "bg-green-100 text-green-700 border-green-200"
                            : "bg-red-100 text-red-700 border-red-200")
                        }
                      >
                        {emp.isActive ? "Active" : "Not Active"}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        {canView && (
                          <IconButton
                            size="small"
                            onClick={() => onView(emp.id)}
                            aria-label="View"
                          >
                            <VisibilityOutlined fontSize="small" />
                          </IconButton>
                        )}
                        {canEdit && (
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(`${ROUTES.EMPLOYEES}/${emp.id}/edit`)
                            }
                            aria-label="Edit"
                          >
                            <EditOutlined fontSize="small" />
                          </IconButton>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={goPrev}
              disabled={page === 1}
              className="w-full sm:w-auto"
            >
              Prev
            </Button>
            <div className="text-sm text-secondary-600 text-center">
              {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of{" "}
              {total}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={goNext}
              disabled={page === totalPages}
              className="w-full sm:w-auto"
            >
              Next
            </Button>
          </div>
        </CardBody>
      </Card>

      <Modal
        open={confirmOpen}
        title="Delete Employee"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={onConfirmDelete}
        onCancel={() => {
          setConfirmOpen(false);
          setDeletingId(null);
        }}
      >
        Are you sure you want to delete this employee?
      </Modal>
    </MainLayout>
  );
};
